/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { AddToCartComponent, IAddToCartResources, OrderDetailsProduct } from '@msdyn365-commerce/components';
import { getUrlSync, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { SalesLine } from '@msdyn365-commerce/retail-proxy';
import { QuickOrderTemplate } from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';

/**
 * The data on which the component should operate.
 */
export interface IAddToCartSelectedData {
    quickOrderTemplate: QuickOrderTemplate | undefined;
}

/**
 * The resources needed to render a buy selected component.
 */
export interface IAddToCartSelectedResources {
    quickOrderGridBuySelectedButtonText?: string;
    quickOrderGridBuySelectedButtonAriaLabel?: string;
    quickOrderGridBuySelectedAddingToCartErrorNotificationTitle?: string;
    quickOrderGridBuySelectedAddingToCartErrorNotificationCloseAriaLabel?: string;
    quickOrderGridGoToCartText?: string;
    quickOrderGridDialogCloseText?: string;
    quickOrderGridSingleItemText?: string;
    quickOrderGridMultiLineItemFormatText?: string;
    quickOrderGridMultiLinesFormatText?: string;
    quickOrderGridHeaderMessageText?: string;
    addedQuantityText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    freePriceText?: string;
}

/**
 * Configuration of a buy selected component.
 */
export interface IAddToCartSelectedProps extends IComponentProps<IAddToCartSelectedData> {
    className: string;
    context: ICoreContext;

    resources: IAddToCartSelectedResources;

    isIcon?: boolean;
}

/**
 * Get the dialog strings.
 * @param props - The buy selected props.
 * @returns The add to cart resources.
 */
export const getDialogStrings = (props: IAddToCartSelectedProps): IAddToCartResources => {
    return {
        goToCartText: props.resources.quickOrderGridGoToCartText ?? '',
        continueShoppingText: props.resources.quickOrderGridDialogCloseText ?? '',
        closeNotificationLabel: props.resources.quickOrderGridBuySelectedAddingToCartErrorNotificationCloseAriaLabel ?? '',
        headerItemOneText: props.resources.quickOrderGridSingleItemText ?? '',
        headerItemFormatText: props.resources.quickOrderGridMultiLineItemFormatText ?? '',
        headerLinesFormatText: props.resources.quickOrderGridMultiLinesFormatText ?? '',
        headerMessageText: props.resources.quickOrderGridHeaderMessageText ?? '',
        freePriceText: props.resources.freePriceText ?? '',
        originalPriceText: props.resources.originalPriceText ?? '',
        currentPriceText: props.resources.currentPriceText ?? '',
        addedQuantityText: props.resources.addedQuantityText ?? '',
        buyAgainNotificationTitle: props.resources.quickOrderGridBuySelectedAddingToCartErrorNotificationTitle ?? '',
        buyAgainNotificationCloseAriaLabel: props.resources.quickOrderGridBuySelectedAddingToCartErrorNotificationCloseAriaLabel ?? ''
    };
};

/**
 * Get the dialog strings.
 * @param quickOrder - The buy selected props.
 * @returns The add to cart resources.
 */
export const getOrderDetails = (quickOrder: QuickOrderTemplate): OrderDetailsProduct[] => {
    const orderDetails: OrderDetailsProduct[] = [];
    for (const quickLine of quickOrder.quickOrderTemplateLines) {
        const quantity = quickLine.productListLine.Quantity;
        const salesLine: SalesLine = {
            Quantity: quantity
        };
        const products = new OrderDetailsProduct(salesLine, quickLine.fullProduct?.ProductDetails);
        orderDetails.push(products);
    }
    return orderDetails;
};

/**
 * Renders a button which adds given products to the cart.
 * @param props - Configuration of the component.
 * @returns A react element or null if no products were given.
 */
export const AddToCartProductListComponent: React.FC<IAddToCartSelectedProps> = (props: IAddToCartSelectedProps): JSX.Element | null => {
    const { data: { quickOrderTemplate } } = props;

    if (quickOrderTemplate?.quickOrderTemplateLines[0].fullProduct?.ProductDetails === undefined) {
        return null;
    }
    const quantity = quickOrderTemplate.quickOrderTemplateLines[0].productListLine.Quantity;
    const buttonLabel = props.resources.quickOrderGridBuySelectedButtonText;
    const buttonAriaLabel = props.resources.quickOrderGridBuySelectedButtonAriaLabel;
    const dialogStrings: IAddToCartResources = getDialogStrings(props);
    const shouldSkipSiteSettings: boolean = true;
    const orderDetailProducts = getOrderDetails(quickOrderTemplate);
    return (
        <AddToCartComponent
            addToCartText={buttonLabel ?? ''}
            outOfStockText=''
            addToCartArialLabel={buttonAriaLabel}
            quantity={quantity}
            id={props.id}
            typeName={props.typeName}
            className={props.className}
            navigationUrl={getUrlSync('cart', props.context.actionContext)}
            data={{ product: quickOrderTemplate.quickOrderTemplateLines[0].fullProduct?.ProductDetails }}
            context={props.context}
            dialogStrings={dialogStrings}
            shouldSkipSiteSettings={shouldSkipSiteSettings}
            orderDetailsProducts={orderDetailProducts}
        />
    );
};
